import {
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Box,
  Grid,
  useTheme,
  useMediaQuery,
  alpha
} from '@material-ui/core';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useAcsMessages from '../../../hooks/acs/useAcsMessages';
import { LogMessage } from '../../../@types/tableRows';

type Props = {
  messageId: string;
  isOpen: boolean;
  onCloseModal: () => void;
};

function AcsMessagesLogDialog({ onCloseModal, isOpen, messageId }: Props) {
  const { t: translate } = useTranslation(['component']);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const handleDialogClose = () => {
    onCloseModal();
  };

  const { getMessageLogByMessageId } = useAcsMessages();

  const [logs, setLogs] = useState<Array<LogMessage>>([]);

  useEffect(() => {
    const log = getMessageLogByMessageId(messageId);
    setLogs(log);
  }, [messageId]);

  const getColor = (status: string): string => {
    switch (status) {
      case 'ERROR':
        return theme.palette.error.main;
      case 'WARNING':
        return theme.palette.warning.main;
      case 'FINISH':
        return theme.palette.success.main;
      case 'INFO':
        return theme.palette.info.main;
      default:
        return theme.palette.text.secondary;
    }
  };

  function formatLogText(logText: string): JSX.Element[] {
    return logText.split('\n').map((line, index) => (
      <React.Fragment key={index}>
        {line}
        <br />
      </React.Fragment>
    ));
  }

  return (
    <Dialog
      open={isOpen}
      onClose={handleDialogClose}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      fullScreen={isMobile}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          [theme.breakpoints.up('lg')]: {
            minWidth: theme.breakpoints.values.lg
          }
        }
      }}
    >
      <DialogTitle id="alert-dialog-title">
        {translate('component:log_history')}
      </DialogTitle>
      <DialogContent>
        <DialogContentText id="alert-dialog-description">
          {logs.length > 0 ? (
            <Box sx={{ p: 2 }}>
              <Grid container spacing={2} sx={{ fontWeight: 'bold', mb: 3 }}>
                <Grid item xs={2}>
                  {translate('component:status')}
                </Grid>
                <Grid item xs={8}>
                  {translate('component:message')}
                </Grid>
                <Grid item xs={2}>
                  {translate('component:step')}
                </Grid>
              </Grid>

              {logs.map((log, index) => {
                const textColor = getColor(log.status);
                const backgroundColor = alpha(textColor, 0.1);

                return (
                  <Grid
                    item
                    container
                    spacing={2}
                    key={index}
                    sx={{
                      mb: 3,
                      backgroundColor:
                        index % 2 === 0 ? 'grey.100' : 'grey.200',
                      borderRadius: 1,
                      py: 1,
                      px: 1
                    }}
                  >
                    <Grid item xs={2} display="flex" alignItems="center">
                      <Box
                        sx={{
                          display: 'inline-block',
                          px: 2,
                          py: 1,
                          borderRadius: 1,
                          bgcolor: backgroundColor,
                          color: textColor
                        }}
                      >
                        <Typography variant="body2" fontWeight={500}>
                          {log.status}
                        </Typography>
                      </Box>
                    </Grid>

                    <Grid item xs={8}>
                      <Typography sx={{ wordBreak: 'break-word' }}>
                        {formatLogText(log.message)}
                      </Typography>
                    </Grid>

                    <Grid item xs={2} display="flex" alignItems="center">
                      <Typography>{log.step}</Typography>
                    </Grid>
                  </Grid>
                );
              })}
            </Box>
          ) : (
            <Box sx={{ p: 2 }}>
              <Typography
                textAlign="center"
                variant="body1"
                sx={{ color: theme.palette.text.secondary }}
              >
                {translate('no_search_result')}
              </Typography>
            </Box>
          )}
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleDialogClose}>
          {translate('component:close')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default AcsMessagesLogDialog;
